<template>
  <v-app>
    <top-bar
      v-if="!showNavigationBar"
      @open-settings="settings = $event"
      :key="updateTopBar"
    />
    <template>
      <router-view></router-view>
    </template>
    <settings-bar
      v-if="settings"
      :active="settings"
      @close-settings="handleCloseSettings"
    >
    </settings-bar>
    <div
      v-for="(activeSnackbar, index) in getActiveSnackbars"
      :key="activeSnackbar.id"
    >
      <snackbar
        :style="{ 'margin-bottom': calcMargin(index) }"
        :snackbar="activeSnackbar"
      ></snackbar>
    </div>
    <warning-dialog
      :dialog="invitationDialog.active"
      :text="invitationDialog.text"
      :title="invitationDialog.title"
      @agree="handleWarningDialogOutput"
      :confirm="$t('app.accept')"
      :reject="$t('app.reject')"
      v-show="false"
    />
  </v-app>
</template>

<script>
import { USER_CRM_REQUEST } from "./store/actions/user";
import { SOCKET_CHECK_MESSAGE_TYPE } from "./store/actions/sockets";
import { mapGetters, mapActions } from "vuex";
import TopBar from "./components/navigation-bar/TopBar.vue";
import api from "@/utils/api.js";
import Snackbar from "@/components/general/Snackbar.vue";
import WarningDialog from "@/components/dialogs/WarningDialog.vue";
import restrictions from "@/utils/restrictions";
import Vue from "vue";
import i18n from "@/utils/translations";
import SettingsBar from "@/components/navigation-bar/SettingsBar.vue";
import router from "./router";
import VueGtag from "vue-gtag";
import Hotjar from "vue-hotjar";

export default {
  components: { TopBar, Snackbar, WarningDialog, SettingsBar },
  name: "App",
  data: () => ({
    invitationDialog: { active: false, title: "", text: "" },
    invite: null,
    settings: false,
    updateTopBar: 0,
    menuAccess: [
      { name: "dashboards" },
      { name: "items" },
      { name: "boats" },
      { name: "dealers" },
    ],
    socket: null,
  }),
  beforeCreate() {
    this.$store.dispatch("GET_THEME");
    this.$store.dispatch("GET_LOGO");
    this.$store.dispatch("GET_CLIENT_IDS");
    this.$store.dispatch("GET_LINKS");
    this.$store.dispatch("GET_ANALYTICS").then((response) => {
      if (response.ga != null && Object.keys(response.ga).length > 0) {
        Vue.use(VueGtag, response.ga, router);
      }

      if (response.hotjar != null && Object.keys(response.hotjar).length > 0) {
        const expireDate = new Date("2023-04-01");
        const now = new Date();
        if (now < expireDate) {
          Vue.use(Hotjar, response.hotjar);
        }
      }
    });
    Vue.moment.locale(i18n.locale);
  },
  created() {
    let isInIframe = () => {
      try {
        return window.self !== window.top;
      } catch (e) {
        return true;
      }
    };
    this.$store.commit("IFRAME", isInIframe());

    try {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        for (let registration of registrations) {
          registration.unregister();
        }
      });
    } catch (error) {
      console.error(JSON.stringify(error));
    }

    if (this.getAccessToken.length !== 0) {
      this.createSocket();
    }
  },

  mounted() {
    this.$store.subscribe((mutation, state) => {
      if (mutation.type === "USER_SHOW_TOS_DIALOG") {
        this.$router.push({
          name: "termsOfService",
        });
      }

      if (mutation.type === "USER_SHOW_TOS_DIALOG_PARTNER") {
        this.$router.push({
          name: "consents",
        });
      }

      if (mutation.type === "USER_SHOW_PENDING_INVITE_DIALOG") {
        if (this.getMe.pending_invites[0].crm_role) {
          this.invitationDialog.title = this.$t("app.welcome");
          this.invitationDialog.text = this.$t(
            "app.youWereInvitedToJoinTheSentinelBoatManagementPlatformYouWereAssignedARole",
            {
              applicationTitle: this.getTheme.title,
              role: this.getMe.pending_invites[0].crm_role.name,
            }
          );
        } else {
          this.invitationDialog.title = this.$t("app.partnerInvitation");
          this.invitationDialog.text = this.$t(
            "app.youWereInvitedToJoinTheSentinelBoatManagementPlatformYouWereAssignedARole",
            {
              applicationTitle: this.getTheme.title,
              member: this.getMe.pending_invites[0].crm_partner.name,
            }
          );
        }

        this.invite = this.getMe.pending_invites[0];
        this.invitationDialog.active = true;
      }
    });
  },

  computed: {
    currentRouteName() {
      return this.$route.name;
    },
    showNavigationBar() {
      let navigationlessList = [
        "login",
        "externalAuth",
        "resetPasswordRequest",
        "resetPassword",
        "register",
        "sharedTask",
        "consents",
        "termsOfService",
        "underMaintenance",
        "noPermission",
      ];
      return (
        this.$route.name === null ||
        navigationlessList.includes(this.$route.name)
      );
    },
    ...mapGetters([
      "getSnackbarStatus",
      "getMe",
      "isAuthenticated",
      "getTheme",
      "getTosAccepted",
      "getTosPartnerAccepted",
      "getAccessToken",
      "getActiveSnackbars",
      "getAnalytics",
    ]),
  },

  methods: {
    calcMargin(i) {
      return i * 60 + "px";
    },
    createSocket() {
      this.socket = new WebSocket(
        process.env.VUE_APP_BASE_WEBSOCKET_URL + this.getAccessToken
      );
      this.socket.onmessage = (event) => {
        const data = JSON.parse(event.data);
        this.$store.dispatch(SOCKET_CHECK_MESSAGE_TYPE, data);
      };
    },
    handleCloseSettings(event, saved) {
      this.settings = event;
      if (saved) {
        this.$router.go();
      }
    },
    handleWarningDialogOutput(agreed) {
      if (agreed) {
        api.acceptInviteUser(this.invite.token).then((resp) => {
          this.navigate();
        });
      } else {
        api.rejectInviteUser(this.invite.token).then((resp) => {
          this.navigate();
        });
      }
    },
    navigate() {
      this.$store.dispatch(USER_CRM_REQUEST).then((resp) => {
        this.invitationDialog = { active: false, title: "", text: "" };

        const nextRoute = restrictions.nextBaseRoute(resp);
        this.$router.push(nextRoute);
      });
    },
  },
};
</script>
